/**
 * Where the recruiter was on the Outreach page, kept across leaving it (Sofia, Oct 15 2026).
 *
 * The page keeps its navigation in the URL (`?posting=`, `?view=` …), which survives the space's
 * 15-minute idle reload — but the sidebar link is a bare `/outreach`, so going to another page and
 * back dropped the open role, the sub-tab and the "All roles" filter. The last query is kept per
 * recruiter in localStorage and put back when the page opens with no query of its own. A link that
 * carries its own (a reminder's `?candidate=`) always wins.
 *
 * Only navigation is kept: ids, a sub-tab name, filter names and the search text — no candidate
 * data. Storage that is blocked or full just means the page opens on the list, as before.
 */
export const NAV_KEYS = ['posting', 'view', 'top', 'scope', 'status', 'q', 'dept', 'owner'] as const;

const storageKey = (email: string) => `ta-outreach-last-view-v1:${email.trim().toLowerCase()}`;

/** The navigation part of a query, in a fixed order, so equal views store equal strings. */
export function navQuery(params: URLSearchParams): string {
  const keep = new URLSearchParams();
  for (const k of NAV_KEYS) {
    const v = params.get(k);
    if (v) keep.set(k, v.slice(0, 200));
  }
  return keep.toString();
}

/** Whether the URL already says where to be — then nothing is restored. */
export const hasOwnNav = (params: URLSearchParams) => Boolean(navQuery(params)) || params.has('candidate');

export function loadNav(email: string): URLSearchParams | null {
  if (!email) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(email));
    if (!raw) return null;
    const restored = new URLSearchParams(navQuery(new URLSearchParams(raw)));
    return restored.toString() ? restored : null;
  } catch {
    return null;
  }
}

export function saveNav(email: string, params: URLSearchParams) {
  if (!email) return;
  try {
    const q = navQuery(params);
    if (q) window.localStorage.setItem(storageKey(email), q);
    else window.localStorage.removeItem(storageKey(email));
  } catch {
    /* private window or blocked storage: the page simply opens on the list next time */
  }
}
